import { cx, type CxOptions, type CxReturn } from 'class-variance-authority';

/**
 * Composes class lists for the atoms: joins strings, arrays and
 * `{ class: condition }` maps and drops falsy inputs. It is CVA's `cx` (clsx)
 * behind one named seam, and it resolves **no** conflicts (ADR 021).
 *
 * Two utilities that set the same property on one element are an authoring
 * defect, not something a runtime helper repairs: Tailwind v4 emits utilities in
 * theme order, so with both present the cascade — never "the one written last"
 * — picks the winner. Atoms therefore set each property exactly once per state
 * (`noWrap` owns `display`; the colour matrix is one compound entry per Forge
 * cell), and `atoms/no-class-conflicts.test.tsx` proves it for every rendered
 * combination with tailwind-merge as a dev-only oracle. A consumer's
 * `className` composes with the atom's classes; when both set one property the
 * cascade, not the order, decides, so neither side can rely on winning. A
 * different look is a variant.
 *
 * tailwind-merge used to sit here (`twMerge(clsx(...))`). It silently dropped
 * `text-text-on-action` beside `text-subtitle2` until taught the token type
 * scale — the bake-off's invisible-label defect (CIT-1287) — and it was the
 * largest runtime dependency of this package after Base UI. Adding a resolver
 * back is one line in this function and breaks no consumer; removing one later
 * would have.
 */
export function cn(...inputs: CxOptions): CxReturn {
  return cx(...inputs);
}
